import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AlertCircle, Loader2, KeyRound } from "lucide-react";
import { AuthLayout, Field } from "@/components/auth/AuthLayout";
import { api, formatApiError } from "@/api/client";
import { useAuth } from "@/context/AuthContext";

export default function Activate() {
    const { t } = useTranslation();
    const [params] = useSearchParams();
    const token = params.get("token") || "";
    const [password, setPassword] = useState("");
    const [password2, setPassword2] = useState("");
    const [err, setErr] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const { login } = useAuth();
    const navigate = useNavigate();

    const submit = async (e: React.FormEvent) => {
        e.preventDefault();
        setErr(null);
        if (password.length < 8) { setErr(t("auth.password_help")); return; }
        if (password !== password2) { setErr(t("auth.activate.password_mismatch")); return; }
        setLoading(true);
        try {
            const r = await api.post("/auth/activate", { token, password });
            // Auto-login with the newly created credentials so the owner lands in onboarding.
            await login(r.data.email, password);
            navigate("/onboarding", { replace: true });
        } catch (e: any) {
            setErr(formatApiError(e));
        } finally { setLoading(false); }
    };

    if (!token) {
        return (
            <AuthLayout title={t("auth.activate.title")}>
                <div className="rounded-md p-4 text-sm" style={{ background: "var(--error-bg)", color: "var(--error-fg)" }} data-testid="activate-missing-token">
                    {t("auth.activate.missing_token")}
                </div>
                <Link to="/login" className="text-sm hover:underline mt-3 inline-block" style={{ color: "var(--accent)" }}>{t("common.back")}</Link>
            </AuthLayout>
        );
    }

    return (
        <AuthLayout title={t("auth.activate.title")}>
            <p className="text-sm mb-4" style={{ color: "var(--text-muted)" }}>{t("auth.activate.intro")}</p>
            <form onSubmit={submit} className="space-y-3" data-testid="activate-form">
                <Field label={t("auth.password")}>
                    <input data-testid="activate-password" type="password" required minLength={8} value={password} onChange={(e) => setPassword(e.target.value)}
                        className="surface-input px-3 py-2 w-full text-sm" style={{ minHeight: 44 }} />
                    <p className="mt-1 text-xs" style={{ color: "var(--text-muted)" }}>{t("auth.password_help")}</p>
                </Field>
                <Field label={t("auth.activate.password_repeat")}>
                    <input data-testid="activate-password2" type="password" required minLength={8} value={password2} onChange={(e) => setPassword2(e.target.value)}
                        className="surface-input px-3 py-2 w-full text-sm" style={{ minHeight: 44 }} />
                </Field>
                {err && (
                    <div className="flex items-start gap-2 rounded-md p-3 text-sm" style={{ background: "var(--error-bg)", color: "var(--error-fg)" }} data-testid="activate-error">
                        <AlertCircle size={16} className="mt-0.5" /><span>{err}</span>
                    </div>
                )}
                <button data-testid="activate-submit" type="submit" disabled={loading} className="w-full inline-flex items-center justify-center gap-2 rounded-md py-2.5 text-sm font-medium disabled:opacity-60" style={{ background: "var(--primary)", color: "var(--primary-fg)", minHeight: 44 }}>
                    {loading ? <Loader2 size={16} className="animate-spin" /> : <KeyRound size={16} />}
                    {t("auth.activate.submit")}
                </button>
            </form>
        </AuthLayout>
    );
}
